import React, { useState, useEffect } from 'react';
import {
  Button,
  Container,
  Row,
  Col,
  Alert,
  Card,
  Spinner,
} from 'react-bootstrap';
import SideMenu from '../components/common/SideMenu';
import AdminMenuButton from '../components/common/AdminMenuButton';
import './Common.scss';
import reservationService from '../services/reservationService';
import { Reservation } from '../models/reserve';
import ReservationTable from '../components/reservation/ReservationTable';

const Reservations: React.FC = () => {
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [isSideMenuOpen, setIsSideMenuOpen] = useState<boolean>(true);

  const handleSideMenuToggle = (isOpen: boolean): void => {
    setIsSideMenuOpen(isOpen);
  };

  // メッセージを自動で消去するヘルパー関数
  const clearMessagesAfterDelay = (): void => {
    setTimeout(() => {
      setError('');
      setSuccess('');
    }, 5000); // 5秒後に自動消去
  };

  // 予約一覧を取得する関数
  const fetchReservations = async (
    showLoader: boolean = false
  ): Promise<void> => {
    if (showLoader) setLoading(true);
    try {
      const response = await reservationService.getReservations();
      setReservations(response);
      setError('');
    } catch (err) {
      console.error('予約一覧取得エラー:', err);
      setError('予約一覧の取得に失敗しました。');
    } finally {
      setLoading(false);
    }
  };

  // 予約更新の処理
  const handleUpdateReservation = async (
    reservation: Reservation
  ): Promise<void> => {
    try {
      await reservationService.updateReservation(reservation);
      setSuccess('予約情報を更新しました。');
      clearMessagesAfterDelay();
      // 一覧を再取得
      await fetchReservations();
    } catch (err) {
      console.error('予約更新エラー:', err);
      setError('予約情報の更新に失敗しました。');
      clearMessagesAfterDelay();
    }
  };

  // 予約削除の処理
  const handleDeleteReservation = async (
    reservationId: number
  ): Promise<void> => {
    try {
      await reservationService.deleteReservation(reservationId, true);
      setSuccess('予約を削除しました。');
      clearMessagesAfterDelay();
      // 一覧を再取得
      await fetchReservations();
    } catch (err) {
      console.error('予約削除エラー:', err);
      setError('予約の削除に失敗しました。');
      clearMessagesAfterDelay();
    }
  };

  // 更新ボタンの処理
  const handleRefresh = (): void => {
    fetchReservations(true);
  };

  // 統計情報を計算
  const getStats = () => {
    const total = reservations.length;
    const pending = reservations.filter(r => r.status === 'pending').length;
    const confirmed = reservations.filter(r => r.status === 'confirmed').length;
    const completed = reservations.filter(r => r.status === 'completed').length;
    const cancelled = reservations.filter(r => r.status === 'cancelled').length;

    return { total, pending, confirmed, completed, cancelled };
  };

  const stats = getStats();

  // 初期ロード時に予約一覧を取得
  useEffect(() => {
    fetchReservations();
  }, []);

  return (
    <div className="page-layout">
      <AdminMenuButton
        onToggle={handleSideMenuToggle}
        isOpen={isSideMenuOpen}
      />

      <Container fluid>
        <Row className="min-vh-100 g-0">
          {/* サイドバー: 条件付き表示 */}
          {isSideMenuOpen && (
            <Col xs={12} md={3} className="admin-sidebar-col">
              <div className="p-3 position-sticky" style={{ top: '1rem' }}>
                <SideMenu />
              </div>
            </Col>
          )}

          {/* メインコンテンツ: サイドバーの状態に応じて幅を調整 */}
          <Col
            xs={12}
            md={isSideMenuOpen ? 9 : 12}
            className="py-4 admin-main-content"
          >
            <div className="px-3">
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h2 className="text-success white-stroke">予約管理</h2>
                <Button
                  variant="outline-success"
                  onClick={handleRefresh}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        className="me-2"
                      />
                      更新中...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-arrow-clockwise me-2"></i>
                      更新
                    </>
                  )}
                </Button>
              </div>

              {/* エラーメッセージ */}
              {error && (
                <Alert
                  variant="danger"
                  dismissible
                  onClose={() => setError('')}
                >
                  {error}
                </Alert>
              )}

              {/* 成功メッセージ */}
              {success && (
                <Alert
                  variant="success"
                  dismissible
                  onClose={() => setSuccess('')}
                >
                  {success}
                </Alert>
              )}

              {/* 統計情報 */}
              <Row className="mb-4">
                <Col md={2}>
                  <Card className="text-center border-primary">
                    <Card.Body>
                      <h5 className="text-primary">{stats.total}</h5>
                      <small>総予約数</small>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={2}>
                  <Card className="text-center border-warning">
                    <Card.Body>
                      <h5 className="text-warning">{stats.pending}</h5>
                      <small>保留中</small>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={2}>
                  <Card className="text-center border-info">
                    <Card.Body>
                      <h5 className="text-info">{stats.confirmed}</h5>
                      <small>確定</small>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={2}>
                  <Card className="text-center border-success">
                    <Card.Body>
                      <h5 className="text-success">{stats.completed}</h5>
                      <small>完了</small>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={2}>
                  <Card className="text-center border-danger">
                    <Card.Body>
                      <h5 className="text-danger">{stats.cancelled}</h5>
                      <small>キャンセル</small>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              {/* 予約一覧テーブル */}
              <Card>
                <Card.Header className="bg-success text-white">
                  <h5 className="mb-0">
                    <i className="bi bi-calendar-check me-2"></i>
                    予約一覧
                  </h5>
                </Card.Header>
                <Card.Body className="p-0">
                  {loading ? (
                    <div className="text-center py-5">
                      <Spinner
                        animation="border"
                        role="status"
                        className="text-success"
                      >
                        <span className="visually-hidden">読み込み中...</span>
                      </Spinner>
                      <div className="mt-2">予約データを読み込み中...</div>
                    </div>
                  ) : (
                    <ReservationTable
                      reservations={reservations}
                      onUpdateReservation={handleUpdateReservation}
                      onDeleteReservation={handleDeleteReservation}
                    />
                  )}
                </Card.Body>
              </Card>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default Reservations;
